// src/pages/Menu/Articulacion/Documents/ProjectsDetails/ProjectDetailsComponents.jsx
import React from "react";

/* ---------- Cards & utils ---------- */
export function GradientCard({ children, className = "", style }) {
  return (
    <div
      className={`rounded-2xl p-[2px] ${className}`}
      style={{ background:"linear-gradient(135deg,#E6F0FF 0%,#B8E6FF 50%,#E6FBFF 100%)", ...style }}
    >
      <div className="rounded-2xl bg-white ring-1 ring-black/5 shadow-sm overflow-visible">{children}</div>
    </div>
  );
}

/* Card con header gradiente */
export function AccentCard({ titleSlot, children, className = "", compactHeader = false }) {
  return (
    <div className={`rounded-2xl overflow-hidden shadow-[0_18px_38px_-18px_rgba(2,6,23,0.55)] flex flex-col ${className}`}>
      <div
        className={`px-5 text-white shrink-0 ${compactHeader ? 'py-2' : 'py-3'}`}
        style={{ background: "linear-gradient(90deg,#0c3b66,#0488AF)" }}
      >
        {titleSlot}
      </div>
      <div className="p-[1.5px] flex-1 flex flex-col min-h-0" style={{ background: "linear-gradient(135deg,#E6F7FF 0%,#F4FAFF 100%)" }}>
        <div className="rounded-b-2xl bg-white flex-1 min-h-0 overflow-hidden">{children}</div>
      </div>
    </div>
  );
}

/* Segmented — switch Resumen / Minutas */
export function Segmented({ value, onChange, options }) {
  return (
    <div className="inline-flex p-0.5 rounded-xl bg-slate-100/70 border border-slate-200">
      {options.map((opt) => {
        const active = value === opt.value;
        return (
          <button
            key={opt.value}
            onClick={() => onChange(opt.value)}
            className={[
              "px-3 h-8 rounded-lg text-xs font-medium transition focus:outline-none",
              active
                ? "text-white shadow-md border border-transparent"
                : "text-slate-700 hover:bg-white/70 border border-transparent",
            ].join(" ")}
            style={ active ? { background: "linear-gradient(90deg,#0c3b66 0%, #0488AF 100%)" } : undefined }
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Skeleton ---------- */
export function Skeleton({ className = "" }) {
  return <div className={`animate-pulse rounded-lg bg-gray-100 ${className}`} />;
}

/* ---------- Utilidades ---------- */
export const csvToArr = (csv = "") => String(csv || "").split(",").map((s) => s.trim()).filter(Boolean);

/* ---- Fechas ---- */
const parseDateToLocal = (dateString) => {
  if (!dateString) return null;
  const clean = String(dateString).split("T")[0];
  const parts = clean.split("-");
  if (parts.length !== 3) return null;
  const [year, month, day] = parts.map(Number);
  if (isNaN(year) || isNaN(month) || isNaN(day)) return null;
  return new Date(year, month - 1, day);
};
const toDate = (v) => (v instanceof Date ? v : typeof v === "string" ? parseDateToLocal(v) : null);
const _rmDot = (s) => s.replace(/\.$/, "");
export const fmtOnlyDate = (d) => { const D = toDate(d); return D ? D.toLocaleDateString("es-CL") : ""; };
const fmtDM = (d) => { const D = toDate(d); return D ? _rmDot(new Intl.DateTimeFormat("es-CL", { day:"numeric", month:"short" }).format(D)) : ""; };
const fmtDMY = (d) => { const D = toDate(d); return D ? _rmDot(new Intl.DateTimeFormat("es-CL", { day:"numeric", month:"short", year:"numeric" }).format(D)) : ""; };
const fmtMonY = (d) => { const D = toDate(d); return D ? `${_rmDot(new Intl.DateTimeFormat("es-CL",{month:"short"}).format(D))} ${D.getFullYear()}` : ""; };
export const fmtNiceRange = (a,b) => {
  const A = toDate(a), B = toDate(b);
  if (!A && !B) return "";
  if (A && !B) return fmtDMY(A);
  if (!A && B) return fmtDMY(B);
  const sameYear = A.getFullYear() === B.getFullYear();
  const sameMonth = sameYear && A.getMonth() === B.getMonth();
  if (sameMonth) return `${A.getDate()}–${B.getDate()} ${fmtMonY(A)}`;
  if (sameYear) return `${fmtDM(A)} – ${fmtDM(B)} ${A.getFullYear()}`;
  return `${fmtDMY(A)} – ${fmtDMY(B)}`;
};

/* Utilidad para formatear texto */
const pretty = (slug = "") =>
  String(slug || "")
    .toLocaleLowerCase("es-CL")
    .replace(/_/g, " ")
    .replace(/(^|\s)(\p{L})/gu, (_, sp, ch) => sp + ch.toLocaleUpperCase("es-CL"));

/* Estados + estilos (proyectos) */
const STATUS_CFG = {
  publicado:   { from: "#60A5FA", to: "#2563EB", text: "text-blue-900",    pill: "bg-blue-50 border-blue-100" },
  en_proceso:  { from: "#34D399", to: "#059669", text: "text-emerald-900", pill: "bg-emerald-50 border-emerald-100" },
  finalizado:  { from: "#F59E0B", to: "#D97706", text: "text-amber-900",   pill: "bg-amber-50 border-amber-100" },
  diseño:      { from: "#C4B5FD", to: "#8B5CF6", text: "text-violet-900",  pill: "bg-violet-50 border-violet-100" },
  en_pausa:    { from: "#9CA3AF", to: "#6B7280", text: "text-gray-900",    pill: "bg-gray-50 border-gray-200" },
  cancelado:   { from: "#FCA5A5", to: "#EF4444", text: "text-red-900",     pill: "bg-red-50 border-red-100" },
};
export const statusCfg = (s) => STATUS_CFG[s] || STATUS_CFG.publicado;
export const STATUS_LABELS = { 
  publicado: "Publicado",
  en_proceso: "En Proceso",
  finalizado: "Finalizado",
  diseño: "Diseño", 
  en_pausa: "En pausa", 
  cancelado: "Cancelado" 
};
export const getStatusLabel = (status) => STATUS_LABELS[status] || pretty(status);

